/**
 * <pg-contact-form>: friendly validation for the contact form (sections/pg-contact.liquid).
 *
 * The browser's own constraints (required, type=email, pattern) decide validity; this only
 * replaces the native bubbles with our messages under each field: red text with an icon,
 * aria-invalid (which shakes the field once, see pg-base.css), an error summary and focus on
 * the first problem. Fields re-check as the shopper fixes them. On a valid submit the button
 * shows its busy state and the form posts normally (Shopify handles spam and the email).
 * After a successful post, focus moves to the success message so screen readers hear it.
 */
import { PgElement, define } from '@pg/core';

class PgContactForm extends PgElement {
  mount() {
    const form = this.closest('form');
    if (!form) return;
    form.noValidate = true;
    this.fields = /** @type {(HTMLInputElement|HTMLTextAreaElement)[]} */ ([...form.querySelectorAll('input.pg-input, textarea.pg-textarea, input.pg-contact__privacy')]);

    form.addEventListener('submit', (event) => this.#submit(event), { signal: this.signal });
    for (const field of this.fields) {
      field.addEventListener('blur', () => field.type !== 'checkbox' && field.value && this.#check(field), { signal: this.signal });
      field.addEventListener('change', () => field.type === 'checkbox' && this.#check(field), { signal: this.signal });
      field.addEventListener(
        'input',
        () => {
          if (field.getAttribute('aria-invalid') === 'true') this.#check(field);
        },
        { signal: this.signal },
      );
    }
  }

  /** @param {SubmitEvent} event */
  #submit(event) {
    const invalid = this.fields.filter((field) => !this.#check(field, true));
    this.refs.summary.hidden = invalid.length === 0;
    if (invalid.length) {
      event.preventDefault();
      invalid[0].focus();
      return;
    }
    this.refs.submit?.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {HTMLInputElement|HTMLTextAreaElement} field
   * @param {boolean} [replay] restart the shake animation on repeated submits
   * @returns {boolean}
   */
  #check(field, replay = false) {
    const error = document.getElementById(`${field.id}-error`);
    const valid = field.checkValidity();
    if (replay && !valid) {
      field.removeAttribute('aria-invalid');
      void field.offsetWidth; // restart the CSS animation
    }
    if (valid) {
      field.removeAttribute('aria-invalid');
      if (error) error.hidden = true;
    } else {
      field.setAttribute('aria-invalid', 'true');
      if (error) {
        error.querySelector('span').textContent = this.#message(field);
        error.hidden = false;
      }
    }
    return valid;
  }

  /** @param {HTMLInputElement|HTMLTextAreaElement} field */
  #message(field) {
    const d = this.dataset;
    if (field.type === 'email') return field.validity.valueMissing ? d.msgEmailRequired : d.msgEmail;
    if (field.type === 'tel') return d.msgPhone;
    if (field.type === 'checkbox') return d.msgPrivacy;
    if (field.tagName === 'TEXTAREA') return d.msgMessage;
    return d.msgName;
  }
}

define('pg-contact-form', PgContactForm);

// Success state: announce and bring it into view after the post-redirect.
const success = document.querySelector('[data-pg-contact-success]');
if (success instanceof HTMLElement) {
  success.focus({ preventScroll: true });
  success.scrollIntoView({ block: 'center' });
}
